import { api } from '@packages/backend/convex/_generated/api';
import { useConvex } from 'convex/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getWorldlineCustomerDevice } from '../WorldlinePaymentForm';
import { setWorldlineRedirectState } from '../worldlineRedirectState';
import { type GooglePayAuthorizationResult, GooglePayButton } from './GooglePayButton';

const GOOGLE_PAY_API_VERSION = 2;
const GOOGLE_PAY_API_VERSION_MINOR = 0;
const GOOGLE_PAY_ALLOWED_AUTH_METHODS = [
  'PAN_ONLY',
  'CRYPTOGRAM_3DS',
] satisfies GooglePayAuthMethod[];

type GooglePayConfig = {
  gateway: string;
  networks: string[];
  gatewayMerchantId: string;
  googleMerchantId: string | null;
  merchantName: string;
};

type SessionGooglePayButtonProps = {
  sessionUuid: string;
  amountCents: number;
  environment: GooglePayEnvironment;
  onSuccess: () => void;
  onError: (message: string) => void;
  onSubmitting?: () => void;
  onCancel?: () => void;
};

const formatDollars = (cents: number): string => (cents / 100).toFixed(2);

const getWorldlineGooglePayToken = (paymentData: GooglePayPaymentData): string => {
  const token = paymentData.paymentMethodData.tokenizationData.token;

  if (!token) {
    throw new Error('Google Pay token is missing encrypted payment data');
  }

  return token;
};

export const SessionGooglePayButton = ({
  sessionUuid,
  amountCents,
  environment,
  onSuccess,
  onError,
  onSubmitting,
  onCancel,
}: SessionGooglePayButtonProps) => {
  const convex = useConvex();
  const [config, setConfig] = useState<GooglePayConfig | null>(null);
  const [configFailed, setConfigFailed] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadConfig = async () => {
      try {
        const result = await convex.action(api.payment.payments.getGooglePayConfig, {
          sessionUuid,
          amountCents,
        });

        if (!isCancelled) setConfig(result);
      } catch (error) {
        // Google Pay unavailable — the card flow remains
        console.warn('Google Pay config failed:', error);
        if (!isCancelled) setConfigFailed(true);
      }
    };

    void loadConfig();

    return () => {
      isCancelled = true;
    };
  }, [amountCents, convex, sessionUuid]);

  const paymentDataRequest = useMemo<GooglePayPaymentDataRequest | null>(() => {
    if (!config) return null;

    return {
      apiVersion: GOOGLE_PAY_API_VERSION,
      apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: GOOGLE_PAY_ALLOWED_AUTH_METHODS,
            allowedCardNetworks: config.networks,
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: config.gateway,
              gatewayMerchantId: config.gatewayMerchantId,
            },
          },
        },
      ],
      merchantInfo: {
        merchantName: config.merchantName,
        ...(config.googleMerchantId ? { merchantId: config.googleMerchantId } : {}),
      },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: formatDollars(amountCents),
        currencyCode: 'AUD',
        countryCode: 'AU',
      },
    };
  }, [amountCents, config]);

  const authorizePayment = useCallback(
    async (paymentData: GooglePayPaymentData): Promise<GooglePayAuthorizationResult> => {
      const encryptedPaymentData = getWorldlineGooglePayToken(paymentData);
      // PAN_ONLY tokens go through 3DS, which requires browser/device data
      const customerDevice = await getWorldlineCustomerDevice();
      const result = await convex.action(api.payment.payments.createGooglePayPayment, {
        sessionUuid,
        amountCents,
        encryptedPaymentData,
        customerDevice,
      });

      if (result.status === 'SUCCESS') {
        return { status: 'SUCCESS' };
      }

      // PAN_ONLY tokens may require a 3DS challenge at the customer's bank;
      // the payment completes via webhook + the payment-callback page
      if (result.status === 'REDIRECT') {
        setWorldlineRedirectState(sessionUuid, {
          paymentGatewayPaymentId: result.paymentGatewayPaymentId,
          amountCents,
        });
        window.location.assign(result.redirectUrl);
        return { status: 'REDIRECT' };
      }

      return {
        status: 'FAILED',
        errorMessage: result.errorMessage,
      };
    },
    [amountCents, convex, sessionUuid],
  );

  if (configFailed) {
    return null;
  }

  return (
    <GooglePayButton
      environment={environment}
      paymentDataRequest={paymentDataRequest}
      authorizePayment={authorizePayment}
      onSuccess={onSuccess}
      onError={onError}
      onSubmitting={onSubmitting}
      onCancel={onCancel}
    />
  );
};
